<template>
    <RadioGroupRoot
        v-bind="controlAttrs"
        :orientation="appearance === 'scale' ? 'horizontal' : orientation"
        :disabled="disabled"
        :aria-labelledby="field?.labelId ?? (label ? labelId : undefined)"
        class="radio-group"
        :class="{ 'is-card': appearance === 'cards', 'is-scale': appearance === 'scale', 'is-unanchored': appearance === 'scale' && !labelStart && !labelEnd }"
        :style="optionsCount ? { '--radio-options-count': optionsCount } : undefined">
        <span v-if="label" :id="labelId" class="group-label">{{ label }}</span>
        <label v-for="option of options" :key="String(option.value)" :for="itemIds[String(option.value)]" class="option" :class="{ 'is-disabled': option.disabled || disabled }">
            <RadioGroupItem
                :id="itemIds[String(option.value)]"
                :value="option.value"
                :disabled="option.disabled"
                :aria-label="appearance === 'scale' ? option.label : undefined"
                class="radio">
                <span v-if="appearance === 'cards'">{{ option.label }}</span>
                <RadioGroupIndicator v-else-if="appearance === 'default'" class="indicator" />
            </RadioGroupItem>
            <span v-if="appearance === 'default'" class="option-label">{{ option.label }}</span>
            <span v-else-if="appearance === 'scale' && optionLabels === 'visible'" class="point-label" aria-hidden="true">{{ option.label }}</span>
        </label>
        <div v-if="appearance === 'scale' && (labelStart || labelEnd)" class="scale-ends">
            <span class="scale-end" aria-hidden="true">{{ labelStart }}</span>
            <span :id="endLabelsId" class="sr-only">{{ endLabelsDescription }}</span>
            <span class="scale-end" aria-hidden="true">{{ labelEnd }}</span>
        </div>
    </RadioGroupRoot>
</template>

<script setup lang="ts">
import { type AcceptableValue, RadioGroupIndicator, RadioGroupItem, RadioGroupRoot } from "reka-ui";
import { computed, useId } from "vue";
import { useI18n } from "vue-i18n";

import { useFormField } from "./formFieldContext";

export type RadioGroupOrientation = "vertical" | "horizontal";

export type RadioGroupAppearance = "default" | "cards" | "scale";

export type RadioGroupOption = {
    value: AcceptableValue;
    label: string;
    disabled?: boolean;
};

type BaseRadioGroupProps = {
    options: RadioGroupOption[];
    label?: string;
    orientation?: RadioGroupOrientation;
    disabled?: boolean;
    /**
     * `scale` is a Likert scale: the options as one evenly spaced row of points, with
     * `labelStart` and `labelEnd` anchoring the ends. It is still a radiogroup of radios;
     * only the presentation changes.
     */
    appearance?: RadioGroupAppearance;
    /** Scale only: text under the first point, e.g. "Strongly disagree". */
    labelStart?: string;
    /** Scale only: text under the last point, e.g. "Strongly agree". */
    labelEnd?: string;
    /**
     * Scale only: whether each option's label also shows under its point (it stays the
     * point's accessible name either way). Hidden by default, since the end labels usually
     * say enough. With anchors the labels show from `md` up and the anchors carry the
     * meaning below that; without anchors they show at every width, since nothing else
     * says what a point means. The labels do not widen the columns, so keep them short.
     */
    optionLabels?: "hidden" | "visible";
};

const { options, label, orientation = "vertical", disabled = false, appearance = "default", labelStart, labelEnd, optionLabels = "hidden" } = defineProps<BaseRadioGroupProps>();

const { t } = useI18n();

const labelId = useId();
const endLabelsId = useId();

// Inside a FormField the group is named by the field's label (a `for` cannot reach a radiogroup)
// and takes its id, describedby and invalid wiring. An `id` or `aria-labelledby` passed as an
// attribute still wins, since fallthrough attrs override these bindings.
const field = useFormField();

// A scale's end labels describe the group too, after the field's hint or error.
const controlAttrs = computed(() => {
    const attrs = { ...field?.controlAttrs.value };
    const describedBy = [attrs["aria-describedby"], appearance === "scale" && (labelStart || labelEnd) ? endLabelsId : undefined].filter(Boolean).join(" ");
    return describedBy ? { ...attrs, "aria-describedby": describedBy } : attrs;
});

// The visible anchors sit apart at the ends, so the description joins them into one phrase,
// "Strongly disagree to Strongly agree", rather than reading them back to back.
const endLabelsDescription = computed(() => (labelStart && labelEnd ? t("components.radioGroup.scaleRange", { start: labelStart, end: labelEnd }) : (labelStart ?? labelEnd)));

const optionsCount = computed(() => {
    if (appearance === "cards") {
        return orientation === "horizontal" ? options.length : 1;
    }
    return appearance === "scale" ? options.length : undefined;
});

const itemIds = computed<Record<string, string>>(() => Object.fromEntries(options.map((option) => [String(option.value), `${labelId}-${String(option.value)}`])));
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    .radio-group {
        @apply flex flex-col gap-3 aria-[orientation=horizontal]:flex-row aria-[orientation=horizontal]:flex-wrap;
    }

    .group-label {
        @apply body-small-bold text-control-label;
    }

    .option {
        @apply flex flex-row items-center gap-3;
    }

    .option.is-disabled {
        @apply cursor-not-allowed;
    }

    .radio {
        @apply flex h-5 min-w-5 max-w-5 shrink-0 appearance-none items-center justify-center rounded-full border border-control-border-strong bg-control-bg transition-colors data-[state=checked]:border-control-accent data-disabled:cursor-not-allowed data-disabled:opacity-50;
    }

    .indicator {
        @apply flex h-full w-full items-center justify-center after:block after:h-2.5 after:w-2.5 after:rounded-full after:bg-control-accent after:content-[''];
    }

    .option-label {
        @apply body select-none text-control-label;
    }

    .option.is-disabled .option-label {
        @apply text-control-label-disabled;
    }

    .radio-group.is-card {
        @apply grid gap-2;
        grid-template-columns: repeat(var(--radio-options-count), minmax(0, 1fr));
    }

    .is-card .group-label {
        @apply col-span-full body-small-semibold;
    }

    .is-card .radio {
        @apply min-h-11 h-auto w-full min-w-0 max-w-none rounded-lg px-3 py-2 body-small-semibold text-control-text-strong border-control-border cursor-pointer hover:bg-surface-hover focus-field;
        &[data-state="checked"] {
            @apply border-control-accent bg-select-option-selected text-control-text-strong ring-1 ring-control-accent;
        }
        &:focus-visible {
            outline: 2px solid var(--color-control-accent);
            outline-offset: 2px;
        }
    }

    /* Scale: one column per point, as wide as the point, spread with space-between so the
       outer points sit flush with the ends of the row, under the anchors. Nothing else sizes
       the columns: the group label and anchors span the row with inline-size containment, and
       each option is contained at the point's width, so the points stay evenly spaced. When
       the row is narrower than the points and their gaps it overflows rather than squeezing
       the points together. */
    .radio-group.is-scale {
        @apply grid w-full gap-x-2 gap-y-2;
        grid-template-columns: repeat(var(--radio-options-count), auto);
        justify-content: space-between;
    }

    .is-scale .group-label {
        @apply col-span-full;
        contain: inline-size;
    }

    .is-scale .option {
        @apply flex-col items-center gap-1 min-w-8;
        contain: inline-size;
    }

    .is-scale .option:first-of-type {
        @apply items-start;
    }

    .is-scale .option:last-of-type {
        @apply items-end;
    }

    .is-scale .radio {
        @apply h-8 min-w-8 max-w-8 border-2 cursor-pointer transition-[background-color,border-color,scale] duration-(--motion-duration-fast) motion-reduce:transition-[background-color,border-color] motion-safe:hover:scale-110 data-[state=checked]:bg-control-accent data-disabled:cursor-not-allowed data-disabled:hover:scale-100;
        &:focus-visible {
            outline: 2px solid var(--color-control-accent);
            outline-offset: 2px;
        }
    }

    /* The point's label shows under it from md up; below that only the end labels show. It
       overflows the point's column, centered under the point, and the outer labels align
       with the row's ends. A scale with no end labels keeps the point labels at every
       width, narrower, since they are all that says what a point means. */
    .point-label {
        @apply sr-only md:not-sr-only md:w-max md:max-w-24 md:details md:text-center md:text-control-label;
    }

    .is-unanchored .point-label {
        @apply not-sr-only w-max max-w-16 details text-center text-control-label;
    }

    .option:first-of-type .point-label {
        @apply text-start;
    }

    .option:last-of-type .point-label {
        @apply text-end;
    }

    .option.is-disabled .point-label {
        @apply text-control-label-disabled;
    }

    .scale-ends {
        @apply col-span-full flex justify-between gap-2;
        contain: inline-size;
    }

    .scale-end {
        @apply details text-control-label;
    }

    /* BaseInput's invalid border, on every choice not yet picked; declared last so it also
       overrides the card and scale borders. */
    .radio-group[aria-invalid="true"] .radio:not([data-state="checked"]) {
        @apply border-status-error;
    }
}
</style>
